var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { observer, useField } from '@formily/react';
import { isArr, isValid, toArr } from '@formily/shared';
import { Space, Tag } from 'antd';
import cls from 'classnames';
import React, { createContext, useContext } from 'react';
import { formatDayjsValue, usePrefixCls } from '../__builtins__';
import useStyle from './style';
export var PlaceholderContext = createContext('N/A');
export var Placeholder = PlaceholderContext.Provider;
export var usePlaceholder = function (value) {
    var placeholder = useContext(PlaceholderContext) || 'N/A';
    return isValid(value) && value !== '' ? value : placeholder;
};
var Input = observer(function (props) {
    var prefixCls = usePrefixCls('form-text', props);
    var _a = __read(useStyle(prefixCls), 2), wrapSSR = _a[0], hashId = _a[1];
    return wrapSSR(React.createElement(Space, { className: cls(prefixCls, hashId, props.className), style: props.style },
        props.addonBefore,
        props.prefix,
        usePlaceholder(props.value),
        props.suffix,
        props.addonAfter));
});
var NumberPicker = observer(function (props) {
    var prefixCls = usePrefixCls('form-text', props);
    var _a = __read(useStyle(prefixCls), 2), wrapSSR = _a[0], hashId = _a[1];
    return wrapSSR(React.createElement(Space, { className: cls(prefixCls, hashId, props.className), style: props.style },
        props.addonBefore,
        props.prefix,
        usePlaceholder(props.formatter
            ? props.formatter(String(props.value), {
                userTyping: false,
                input: '',
            })
            : props.value),
        props['suffix'],
        props.addonAfter));
});
var Select = observer(function (props) {
    var _a, _b;
    var field = useField();
    var prefixCls = usePrefixCls('form-text', props);
    var _c = __read(useStyle(prefixCls), 2), wrapSSR = _c[0], hashId = _c[1];
    var dataSource = ((_a = field === null || field === void 0 ? void 0 : field.dataSource) === null || _a === void 0 ? void 0 : _a.length)
        ? field.dataSource
        : ((_b = props === null || props === void 0 ? void 0 : props.options) === null || _b === void 0 ? void 0 : _b.length)
            ? props.options
            : [];
    var placeholder = usePlaceholder();
    var getSelected = function () {
        var value = props.value;
        if (props.mode === 'multiple' || props.mode === 'tags') {
            if (props.labelInValue) {
                return isArr(value) ? value : [];
            }
            else {
                return isArr(value)
                    ? value.map(function (val) { return ({ label: val, value: val }); })
                    : [];
            }
        }
        else {
            if (props.labelInValue) {
                return isValid(value) ? [value] : [];
            }
            else {
                return isValid(value) ? [{ label: value, value: value }] : [];
            }
        }
    };
    var getLabel = function (target) {
        var _a, _b;
        var labelKey = ((_a = props.fieldNames) === null || _a === void 0 ? void 0 : _a.label) || 'label';
        return (((_b = dataSource === null || dataSource === void 0 ? void 0 : dataSource.find(function (item) {
            var _a;
            var valueKey = ((_a = props.fieldNames) === null || _a === void 0 ? void 0 : _a.value) || 'value';
            return item[valueKey] == (target === null || target === void 0 ? void 0 : target.value);
        })) === null || _b === void 0 ? void 0 : _b[labelKey]) ||
            target.label ||
            placeholder);
    };
    var getLabels = function () {
        var selected = getSelected();
        if (!selected.length)
            return placeholder;
        if (selected.length === 1)
            return getLabel(selected[0]);
        return selected.map(function (item, key) {
            return React.createElement(Tag, { key: key }, getLabel(item));
        });
    };
    return wrapSSR(React.createElement("div", { className: cls(prefixCls, hashId, props.className), style: props.style }, getLabels()));
});
var TreeSelect = observer(function (props) {
    var _a, _b;
    var field = useField();
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var _c = __read(useStyle(prefixCls), 2), wrapSSR = _c[0], hashId = _c[1];
    var dataSource = ((_a = field === null || field === void 0 ? void 0 : field.dataSource) === null || _a === void 0 ? void 0 : _a.length)
        ? field.dataSource
        : ((_b = props === null || props === void 0 ? void 0 : props.treeData) === null || _b === void 0 ? void 0 : _b.length)
            ? props.treeData
            : [];
    var getSelected = function () {
        var value = props.value;
        if (props.multiple) {
            if (props.labelInValue) {
                return isArr(value) ? value : [];
            }
            else {
                return isArr(value)
                    ? value.map(function (val) { return ({ label: val, value: val }); })
                    : [];
            }
        }
        else {
            if (props.labelInValue) {
                return value ? [value] : [];
            }
            else {
                return value ? [{ label: value, value: value }] : [];
            }
        }
    };
    var findLabel = function (value, dataSource, treeNodeLabelProp) {
        var _a, _b;
        var valueKey = ((_a = props.fieldNames) === null || _a === void 0 ? void 0 : _a.value) || 'value';
        for (var i = 0; i < (dataSource === null || dataSource === void 0 ? void 0 : dataSource.length); i++) {
            var item = dataSource[i];
            if ((item === null || item === void 0 ? void 0 : item[valueKey]) === value) {
                return (_b = item === null || item === void 0 ? void 0 : item.label) !== null && _b !== void 0 ? _b : item[treeNodeLabelProp];
            }
            else {
                var childLabel = findLabel(value, item === null || item === void 0 ? void 0 : item.children, treeNodeLabelProp);
                if (childLabel)
                    return childLabel;
            }
        }
    };
    var getLabels = function () {
        var selected = getSelected();
        if (!(selected === null || selected === void 0 ? void 0 : selected.length))
            return React.createElement(Tag, null, placeholder);
        return selected.map(function (_a, key) {
            var value = _a.value, label = _a.label;
            return (React.createElement(Tag, { key: key }, findLabel(value, dataSource, props.treeNodeLabelProp) ||
                label ||
                placeholder));
        });
    };
    return wrapSSR(React.createElement("div", { className: cls(prefixCls, hashId, props.className), style: props.style }, getLabels()));
});
var Cascader = observer(function (props) {
    var _a, _b;
    var field = useField();
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var _c = __read(useStyle(prefixCls), 2), wrapSSR = _c[0], hashId = _c[1];
    var dataSource = ((_a = field === null || field === void 0 ? void 0 : field.dataSource) === null || _a === void 0 ? void 0 : _a.length)
        ? field.dataSource
        : ((_b = props === null || props === void 0 ? void 0 : props.options) === null || _b === void 0 ? void 0 : _b.length)
            ? props.options
            : [];
    var findSelectedItem = function (items, val) {
        return items.find(function (item) { return item.value == val; });
    };
    var findSelectedItems = function (sources, selectedValues) {
        return selectedValues.map(function (value) {
            var result = [];
            var items = sources;
            value.forEach(function (val) {
                var _a, _b;
                var selectedItem = findSelectedItem(items, val);
                result.push({
                    label: (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.label) !== null && _a !== void 0 ? _a : '',
                    value: selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.value,
                });
                items = (_b = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.children) !== null && _b !== void 0 ? _b : [];
            });
            return result;
        });
    };
    var getSelected = function () {
        var val = toArr(props.value);
        // unified conversion to multi selection mode
        return props.multiple ? val : [val];
    };
    var getLabels = function () {
        var selected = getSelected();
        var values = findSelectedItems(dataSource, selected);
        var labels = values
            .map(function (val) {
            return val.map(function (item) { return item.label; }).join('/');
        })
            .join(' ');
        return labels || placeholder;
    };
    return wrapSSR(React.createElement("div", { className: cls(prefixCls, hashId, props.className), style: props.style }, getLabels()));
});
var DatePicker = function (props) {
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var getLabels = function () {
        var labels = formatDayjsValue(props.value, props.format, placeholder);
        return isArr(labels) ? labels.join('~') : labels;
    };
    return React.createElement("div", { className: cls(prefixCls, props.className) }, getLabels());
};
var DateRangePicker = function (props) {
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var getLabels = function () {
        var labels = formatDayjsValue(props.value, props.format, placeholder);
        return isArr(labels) ? labels.join('~') : labels;
    };
    return (React.createElement("div", { className: cls(prefixCls, props.className), style: props.style }, getLabels()));
};
var TimePicker = function (props) {
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var getLabels = function () {
        var labels = formatDayjsValue(props.value, props.format, placeholder);
        return isArr(labels) ? labels.join('~') : labels;
    };
    return (React.createElement("div", { className: cls(prefixCls, props.className), style: props.style }, getLabels()));
};
var TimeRangePicker = function (props) {
    var placeholder = usePlaceholder();
    var prefixCls = usePrefixCls('form-text', props);
    var getLabels = function () {
        var labels = formatDayjsValue(props.value, props.format, placeholder);
        return isArr(labels) ? labels.join('~') : labels;
    };
    return (React.createElement("div", { className: cls(prefixCls, props.className), style: props.style }, getLabels()));
};
var InternalPreviewText = observer(function (props) {
    var prefixCls = usePrefixCls('form-text', props);
    return (React.createElement("div", { className: cls(prefixCls, props.className), style: props.style }, usePlaceholder(props.value)));
});
export var PreviewText = Object.assign(InternalPreviewText, {
    Input: Input,
    Select: Select,
    TreeSelect: TreeSelect,
    Cascader: Cascader,
    DatePicker: DatePicker,
    DateRangePicker: DateRangePicker,
    TimePicker: TimePicker,
    TimeRangePicker: TimeRangePicker,
    Placeholder: Placeholder,
    usePlaceholder: usePlaceholder,
    NumberPicker: NumberPicker,
});
export default PreviewText;
//# sourceMappingURL=index.js.map